import { useState } from "react";

function ContactPage() {
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [phone, setPhone] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (name === "phone") {
      setPhone(value);
    }
  };
  
  const handleSubmit = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    
    // Form validation
    if (phone.trim() !== "" && !phoneConsent) {
      alert("Please consent to receive text messages to continue.");
      return;
    }
    
    if (!termsConsent) {
      alert("Please agree to the Terms and Conditions to continue.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Convert formData to URL-encoded string
      const data = new URLSearchParams();
      for (const [key, value] of Object.entries(formData)) {
        data.append(key, value);
      }
      data.append("phoneConsent", phoneConsent.toString());
      data.append("termsConsent", termsConsent.toString());
      
      const response = await fetch("https://api.southcoaststrategy.com", {
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
        }
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: ""
        });
        setPhoneConsent(false);
        setTermsConsent(false);
        setPhone("");
      } else {
        alert("There was an error submitting your message. Please try again later.");
      }
    } catch (err: any) {
      alert("An error occurred: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const isSubmitDisabled = (phone.trim() !== "" && !phoneConsent) || !termsConsent;
  
  return (
    <main className="bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Title */}
        <h1 
          className="text-4xl font-bold text-center mb-12 text-black" 
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Contact Scott
        </h1>

        {/* Contact Form - Centered */}
        <div className="max-w-2xl mx-auto mb-16">
          {!isSubmitted ? (
            <div className="bg-gray-100 p-8 rounded-lg shadow">
              <h4 className="text-2xl font-bold mb-6 text-black">
                I'd love to hear from you!
              </h4>
              
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-lg font-medium text-black mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-lg font-medium text-black mb-1">
                    Email*
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-lg font-medium text-black mb-1">
                    Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-lg font-medium text-black mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 text-black"
                  />
                </div>
                
                {/* Phone consent checkbox - Always visible */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="text-consent"
                      name="text-consent"
                      type="checkbox"
                      checked={phoneConsent}
                      onChange={() => setPhoneConsent(!phoneConsent)}
                      className="h-5 w-5 text-indigo-900 focus:ring-indigo-900 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="text-consent" className="text-black">
                      I consent to receive text messages from the campaign
                    </label>
                  </div>
                </div>
                
                {/* SMS consent information - Always visible with smaller text */}
                <div className="bg-gray-200 p-4 rounded-md text-black">
                  <div style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
                    <p style={{ fontWeight: "bold", marginBottom: "0.3rem" }}>Text Message Opt-In:</p>
                    <p style={{ marginBottom: "0.3rem" }}>
                      By providing your phone number and clicking 'Submit,' you consent to receive manually sent political 
                      text messages from the Scott Higginbotham Campaign. Message frequency may vary. Standard message and 
                      data rates may apply. Reply STOP to opt out. Reply HELP for help.
                    </p>
                    <p style={{ marginBottom: "0.3rem" }}>
                      <strong>Donations may be solicited</strong> through these text messages.
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      Consent is not a condition of purchase. See our Privacy Policy and Terms and Conditions.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="terms-consent"
                      name="terms-consent"
                      type="checkbox"
                      checked={termsConsent}
                      onChange={() => setTermsConsent(!termsConsent)}
                      required
                      className="h-5 w-5 text-indigo-900 focus:ring-indigo-900 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="terms-consent" className="text-black">
                      I agree to the{" "}
                      <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:text-indigo-900 underline">
                        Terms and Conditions
                      </a>{" "}
                      and{" "}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:text-indigo-900 underline">
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-start">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled || isSubmitting}
                    className={`font-bold py-3 px-8 rounded-full shadow-lg transition ${
                      isSubmitDisabled ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-black text-white hover:bg-indigo-800'
                    }`}
                  >
                    {isSubmitting ? "Sending..." : "Send"}
                  </button>
                </div>
                
                <p className="text-xs text-gray-600 mt-4">
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:text-indigo-900 underline">
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:text-indigo-900 underline">
                    Terms of Service
                  </a>{" "}
                  apply.
                </p>
              </form>
              
              {/* Custom styling for small SMS text */}
              <style dangerouslySetInnerHTML={{__html: `
                .sms-micro-text {
                  font-size: 0.75rem !important;
                  line-height: 1.2 !important;
                }
                
                .sms-micro-text p {
                  font-size: 0.75rem !important;
                  line-height: 1.2 !important;
                  margin-bottom: 0.3rem !important;
                }
                
                .not-prose p {
                  font-size: 0.75rem !important;
                  line-height: 1.2 !important;
                  margin: 0 0 0.3rem 0 !important;
                }
              `}} />
            </div>
          ) : (
            <div className="bg-gray-100 p-8 rounded-lg shadow text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-2xl font-bold mb-2 text-black">Message Sent!</h4>
              <p className="text-black">Thank you for reaching out. We'll be in touch shortly.</p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="mt-6 bg-black text-white font-bold py-2 px-6 rounded-full hover:bg-indigo-800 transition"
              >
                Send Another Message
              </button>
            </div>
          )}
        </div>
        
        {/* Contact Information - Below the Form */}
        <div className="max-w-2xl mx-auto mb-16 bg-gray-100 p-8 rounded-lg shadow">
          <h4 className="text-2xl font-bold mb-6 text-black">Scott For League City</h4>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-lg text-black">info@scottforleaguecity.org</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-lg text-black">League City, TX</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <a 
                href="https://www.facebook.com/ScottForLeagueCity" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-lg text-black hover:text-indigo-700"
              >
                Facebook: @ScottForLeagueCity
              </a>
            </div>
          </div>
          
          <div className="mt-8">
            <h5 className="font-bold text-xl mb-4 text-black">Campaign Donations</h5>
            <p className="mb-4 text-black">Your support helps us reach more voters and spread our message of responsible governance.</p>
            <a
              href="/donate"
              className="inline-block bg-black text-white font-bold py-2 px-6 rounded-full hover:bg-indigo-800 transition"
            >
              Donate to the Campaign
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ContactPage;